import { ApiError } from "@brandex/api-client";
import { MfaCard, useSignOut } from "@brandex/dashboard";
import { formatDateTime } from "@brandex/i18n";
import { Alert, Badge, Button, Spinner } from "@brandex/ui";
import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";

import { useAuditEvents, useOrganizations, useStats, useUsers } from "./data";

/** Renders query errors; `mfa_required` gets the enrollment flow instead. */
export function QueryError({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === "mfa_required") return <MfaRequired />;
  if (error instanceof ApiError && error.code === "forbidden") {
    return <Alert tone="error">Your staff role does not include this area ({error.message}).</Alert>;
  }
  return <Alert tone="error">{errorText(error)}</Alert>;
}

/** Plain-language text for an API or network error. */
export function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code in CONFLICTS) return CONFLICTS[error.code] ?? error.message;
    if (error.code === "validation_failed") return error.field ? `Check ${label(error.field)}: ${error.message}.` : `${error.message}.`;
    if (error.code === "rate_limited") return error.retryAfterSeconds ? `Too many attempts. Try again in ${error.retryAfterSeconds} s.` : "Too many attempts. Try again shortly.";
    if (error.code === "not_found") return "That record no longer exists. Refresh the page.";
    if (error.status >= 500) return "The server had a problem. Try again; if it keeps happening, check the API logs.";
    return error.message;
  }
  // fetch rejects with a TypeError when the API can't be reached.
  if (error instanceof TypeError) return "Can't reach the BrandEx API. Check your connection and try again.";
  return error instanceof Error ? error.message : "Request failed";
}

/** `account_id` → "account id"; nested names keep their last part. */
function label(field: string): string {
  return (field.split(".").pop() ?? field).replace(/_/g, " ");
}

/** The server's message when it rejected `field`, for a form `Field`'s `error`. */
export function fieldError(error: unknown, ...fields: string[]): string | undefined {
  if (!(error instanceof ApiError) || error.code !== "validation_failed" || error.field === undefined) return undefined;
  const leaf = error.field.split(".").pop() ?? error.field;
  return fields.includes(error.field) || fields.includes(leaf) ? error.message : undefined;
}

/** Plain-language messages for conflict codes staff can hit. */
const CONFLICTS: Record<string, string> = {
  domain_taken: "That domain is already connected to another doctor's site.",
  subdomain_taken: "That subdomain is already used by another doctor's site.",
  website_slug_taken: "Another doctor's site already uses that address.",
  plan_slug_taken: "Another plan already uses that slug.",
  component_key_taken: "Another component already uses that key.",
  email_taken: "An account with this email already exists.",
  phone_taken: "That mobile number is already registered.",
  already_onboarded: "This client has already been onboarded.",
  subscription_exists: "This client already has an open package.",
  deliverable_exists: "That item is already planned for this month.",
  deliverable_changed: "Someone else changed this item. Refresh and try again.",
  invoice_changed: "Someone else changed this invoice. Refresh and try again.",
  publication_exists: "That post is already in the publishing log.",
  studio_clash: "The studio is already booked at that time. Choose another time or the chamber.",
  slot_taken: "That appointment time was just taken.",
  below_price_floor: "That price is below the minimum margin. Only a Super admin can approve it, with a reason.",
  quote_expired: "This quote is more than 14 days old. Send a new quote before activating.",
  months_already_billed: "Some of those months are already billed. Choose a later start month or void the other invoice first.",
  template_request_open: "This practice already has an open template request.",
  design_missing: "Set the practice's design before delivering the request.",
  awaiting_payment: "The patient hasn't paid yet. It can be confirmed once paid.",
  refund_exceeds_refundable: "That is more than the rules allow refunding for this invoice.",
};

/** Staff must use an MFA-verified session (spec §9.1). */
function MfaRequired() {
  const signOut = useSignOut();
  return (
    <div className="grid max-w-2xl gap-4">
      <Alert tone="info">
        <p className="flex items-center gap-2 font-medium"><ShieldAlert className="h-4 w-4" aria-hidden />Two-factor authentication is required for staff.</p>
        <p className="mt-1 text-muted-foreground">Set up your authenticator app below (if you haven&apos;t), then sign in again with a code.</p>
      </Alert>
      <MfaCard />
      <div><Button variant="outline" onClick={() => signOut.mutate(false)}>Sign in again</Button></div>
    </div>
  );
}

function PageTitle({ children, note }: { children: ReactNode; note?: string }) {
  return (
    <header className="mb-6">
      <h1 className="font-display text-3xl font-bold tracking-tight">{children}</h1>
      {note !== undefined ? <p className="mt-1 text-sm text-muted-foreground">{note}</p> : null}
    </header>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="glass rounded-[var(--radius-lg)] p-5">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-4xl font-bold tabular-nums">{new Intl.NumberFormat("en").format(value)}</p>
    </div>
  );
}

export function DashboardPage() {
  const stats = useStats();
  return (
    <>
      <PageTitle note="Accounts, sign-ins and security, from platform records; refreshed every minute.">Platform health</PageTitle>
      {stats.isPending ? <Spinner label="Loading…" /> : null}
      {stats.isError ? <QueryError error={stats.error} /> : null}
      {stats.data !== undefined ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="Users" value={stats.data.users_total} />
          <Stat label="Verified emails" value={stats.data.users_verified} />
          <Stat label="Users with 2FA" value={stats.data.users_with_mfa} />
          <Stat label="Organizations" value={stats.data.organizations_total} />
          <Stat label="Active sessions" value={stats.data.active_sessions} />
          <Stat label="Sign-ups (7 days)" value={stats.data.signups_last_7_days} />
        </div>
      ) : null}
    </>
  );
}

function DataTable<T>({ query, columns, rowKey }: { query: UseInfiniteQueryResult<T[]>; columns: { header: string; cell: (row: T) => ReactNode }[]; rowKey: (row: T) => string }) {
  if (query.isPending) return <Spinner label="Loading…" />;
  if (query.isError) return <QueryError error={query.error} />;
  return (
    <div className="glass overflow-x-auto rounded-[var(--radius-lg)]">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>{columns.map((c) => <th key={c.header} scope="col" className="px-4 py-3 font-medium">{c.header}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {query.data.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-muted-foreground">Nothing here yet.</td></tr>
          ) : (
            query.data.map((row) => (
              <tr key={rowKey(row)} className="hover:bg-muted/50">
                {columns.map((c) => <td key={c.header} className="px-4 py-3 align-top">{c.cell(row)}</td>)}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {query.hasNextPage ? (
        <div className="border-t border-border p-3 text-center">
          <Button variant="ghost" size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>Load more</Button>
        </div>
      ) : null}
    </div>
  );
}

const when = (iso: string) => formatDateTime("en", iso);

export function OrganizationsPage() {
  const q = useOrganizations();
  return (
    <>
      <PageTitle note="Viewing this list is recorded in the audit log.">Organizations</PageTitle>
      <DataTable
        query={q}
        rowKey={(o) => o.organization.id}
        columns={[
          { header: "Name", cell: (o) => <span className="font-medium">{o.organization.name}</span> },
          { header: "Slug", cell: (o) => <code className="text-xs text-muted-foreground">{o.organization.slug}</code> },
          { header: "Members", cell: (o) => <span className="tabular-nums">{o.member_count}</span> },
          { header: "Created", cell: (o) => when(o.organization.created_at) },
        ]}
      />
    </>
  );
}

export function UsersPage() {
  const q = useUsers();
  return (
    <>
      <PageTitle note="Viewing this list is recorded in the audit log.">Users</PageTitle>
      <DataTable
        query={q}
        rowKey={(u) => u.id}
        columns={[
          { header: "Name", cell: (u) => <span className="font-medium">{u.display_name}</span> },
          { header: "Email", cell: (u) => <span>{u.email} {u.email_verified ? <Badge tone="success">verified</Badge> : <Badge>unverified</Badge>}</span> },
          { header: "Status", cell: (u) => <Badge tone={u.status === "active" ? "success" : "neutral"}>{u.status}</Badge> },
          { header: "Staff roles", cell: (u) => (u.platform_roles.length > 0 ? u.platform_roles.join(", ") : "—") },
          { header: "2FA", cell: (u) => (u.mfa_enabled ? "On" : "Off") },
          { header: "Joined", cell: (u) => when(u.created_at) },
        ]}
      />
    </>
  );
}

export function AuditPage() {
  const q = useAuditEvents();
  return (
    <>
      <PageTitle note="Append-only record of sensitive actions across all tenants.">Audit log</PageTitle>
      <DataTable
        query={q}
        rowKey={(e) => e.id}
        columns={[
          { header: "When", cell: (e) => <span className="whitespace-nowrap">{when(e.occurred_at)}</span> },
          { header: "Action", cell: (e) => <code className="text-xs">{e.action}</code> },
          { header: "Actor", cell: (e) => <span>{e.actor_kind}{e.actor_user_id !== null && e.actor_user_id !== undefined ? <code className="ml-1 text-xs text-muted-foreground">{e.actor_user_id.slice(-6)}</code> : null}</span> },
          { header: "Target", cell: (e) => (e.target_kind !== null && e.target_kind !== undefined ? `${e.target_kind}:${(e.target_id ?? "").slice(-6)}` : "—") },
          { header: "IP", cell: (e) => e.ip_address ?? "—" },
        ]}
      />
    </>
  );
}
